// Libs
import { UseFormReturn } from "react-hook-form";

// Hooks
import { FormValues } from "@/hooks/useDriverForm";
import { useBranches } from "@/hooks/useBranches";

// Components
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/atoms/form";
import { Input } from "@/components/atoms/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/atoms/select";
import { useEffect } from "react";

interface PersonalInfoFormProps {
  form: UseFormReturn<FormValues>;
  isEditing: boolean;
}

export function PersonalInfoForm({ form, isEditing }: PersonalInfoFormProps) {
  const {
    branches,
    loading: branchesLoading,
    error: branchesError,
    fetchBranches,
  } = useBranches();

  useEffect(() => {
    fetchBranches();
  }, []);

  return (
    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs font-medium text-gray-600">
              Nombre Completo
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                disabled={!isEditing}
                placeholder="Ingrese nombre completo"
              />
            </FormControl>
            <FormMessage className="text-red-500" />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs font-medium text-gray-600">
              Mail
            </FormLabel>
            <FormControl>
              <Input {...field} disabled={true} placeholder="Ingrese email" />
            </FormControl>
            <FormMessage className="text-red-500" />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="address"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs font-medium text-gray-600">
              Domicilio
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                disabled={!isEditing}
                placeholder="Ingrese domicilio"
              />
            </FormControl>
            <FormMessage className="text-red-500" />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="document"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs font-medium text-gray-600">
              Documento de identidad
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                disabled={!isEditing}
                placeholder="Ingrese documento"
              />
            </FormControl>
            <FormMessage className="text-red-500" />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="phone"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs font-medium text-gray-600">
              Teléfono
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                disabled={!isEditing}
                placeholder="Ingrese teléfono"
              />
            </FormControl>
            <FormMessage className="text-red-500" />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="vehicle_type"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs font-medium text-gray-600">
              Vehículo
            </FormLabel>
            <Select
              onValueChange={(value) => {
                field.onChange(value);
              }}
              value={field.value || ""}
              disabled={!isEditing}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="bicycle">Bicicleta</SelectItem>
                <SelectItem value="moto">Moto</SelectItem>
                <SelectItem value="car">Automóvil</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="text-red-500" />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="branch_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs font-medium text-gray-600">
              Sucursal
            </FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value}
              disabled={!isEditing || branchesLoading}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      branchesLoading
                        ? "Cargando sucursales..."
                        : branchesError
                          ? "Error al cargar sucursales"
                          : "Seleccionar sucursal"
                    }
                  />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {branchesLoading ? (
                  <SelectItem value="loading" disabled>
                    Cargando...
                  </SelectItem>
                ) : branchesError ? (
                  <SelectItem value="error" disabled>
                    Error al cargar sucursales
                  </SelectItem>
                ) : branches.length === 0 ? (
                  <SelectItem value="empty" disabled>
                    No hay sucursales disponibles
                  </SelectItem>
                ) : (
                  branches.map((branch) => (
                    <SelectItem key={branch.id} value={String(branch.id)}>
                      {branch.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <FormMessage className="text-red-500" />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="worker_type"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs font-medium text-gray-600">
              Contrato
            </FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value}
              disabled={!isEditing}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="full-time">Full time</SelectItem>
                <SelectItem value="part-time">Part time</SelectItem>
                <SelectItem value="freelance">Freelance</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="text-red-500" />
          </FormItem>
        )}
      />
    </div>
  );
}
